import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { Navigate, useOutletContext } from 'react-router-dom-v5-compat';
import { toggleValues } from '@department-of-veterans-affairs/platform-site-wide/selectors';
import FEATURE_FLAG_NAMES from '@department-of-veterans-affairs/platform-utilities/featureFlagNames';

import { APPEAL_TYPES } from '~/platform/shared/claims-appeals';
import { cancelUpload } from '../actions';
import {
  submitClaimsEvidenceFiles,
  clearSCUploadErrors,
} from '../actions/submitClaimsEvidenceFiles';
import AddFilesForm from '../components/claim-files-tab/AddFilesForm';
import ClaimFilesLayout from '../components/claim-files-tab/ClaimFilesLayout';
import FilesReceived from '../components/claim-files-tab-v2/FilesReceived';
import OtherWaysToSendYourDocuments from '../components/claim-files-tab-v2/OtherWaysToSendYourDocuments';
import {
  DEFAULT_APPEAL_TAB,
  FILE_UPLOAD_GUIDANCE,
  QUICK_SUBMIT_GUIDANCE,
} from '../constants';
import { CLAIMS_EVIDENCE_API_NAME } from '../utils/analytics';
import { SUPPLEMENTAL_CLAIM_DOC_TYPES_FALLBACK } from '../utils/supplementalClaimDocTypes';
import { CE_FILE_TYPES } from '../utils/validations';

// The onSubmit handler AddFilesForm calls: pairs the files the veteran chose
// with the appeal they belong to. Separate so a test can check that pairing.
export const buildAddFilesFormSubmitHandler =
  (submitFiles, appealId) => (files, batchId) =>
    submitFiles({ appealId, files, batchId });

const SupplementalClaimFilesPage = ({
  appeal: propAppeal,
  clearSCErrors,
  failedFiles,
  onCancelUpload,
  progress,
  submitFiles,
  uploading,
  uploadComplete,
}) => {
  // Old tabs: rendered as an Outlet child of AppealInfo, receives appeal via
  // context. New tabs: rendered as a va-tabs panel with appeal as a prop.
  const ctx = useOutletContext() || [];
  const appeal = propAppeal ?? ctx[0];
  const uploadEnabled = useSelector(
    state =>
      toggleValues(state)[
        FEATURE_FLAG_NAMES.cstSupplementalClaimsEvidenceUpload
      ],
  );
  // Remount AddFilesForm after a successful upload so the form clears. A
  // remount rather than a state reset because the file list lives inside
  // va-file-input-multiple, not in React state. `uploadComplete` alone isn't
  // enough: DONE_UPLOADING fires from onAllComplete whichever way the batch
  // went, so a failed batch sets it too. Checking failedFiles is what keeps the
  // veteran's selection after a failure.
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (uploadComplete && !failedFiles.length) {
      setFormKey(key => key + 1);
    }
  }, [uploadComplete, failedFiles]);

  if (!appeal) {
    return null;
  }

  // Prevent direct-URL access when the Files tab isn't applicable
  if (!uploadEnabled || appeal.type !== APPEAL_TYPES.supplementalClaim) {
    return (
      <Navigate to={`/appeals/${appeal.id}/${DEFAULT_APPEAL_TAB}`} replace />
    );
  }

  const documentTypes =
    appeal.attributes?.uploadMetadata?.documentTypeOptions ??
    SUPPLEMENTAL_CLAIM_DOC_TYPES_FALLBACK;

  // Intentionally no "File submissions in progress" section: /v0/claims_evidence
  // is synchronous, so there's no persisted PENDING state to show.
  return (
    <ClaimFilesLayout
      header={
        <>
          <h2 className="tab-header vads-u-margin-y--0">Claim files</h2>
          {FILE_UPLOAD_GUIDANCE}
        </>
      }
      uploadForm={
        <>
          {QUICK_SUBMIT_GUIDANCE}
          <AddFilesForm
            acceptedFileTypes={CE_FILE_TYPES}
            documentTypes={documentTypes}
            evidenceSubmissions={appeal.attributes?.evidenceSubmissions ?? null}
            fileTab
            headerSize={3}
            hideRequiredIndicator
            key={formKey}
            onCancel={onCancelUpload}
            onSubmit={(files, batchId) => {
              clearSCErrors();
              return buildAddFilesFormSubmitHandler(submitFiles, appeal.id)(
                files,
                batchId,
              );
            }}
            progress={progress}
            uploading={uploading}
            usingClaimsEvidenceAPI
          />
        </>
      }
      filesReceived={
        <FilesReceived
          claim={appeal}
          title="Files received in the last 60 days"
          description="If you submitted files using this tool, they’ll appear here. Files submitted by mail or in person don’t appear here."
          emptyStateText="We haven’t received any files in the last 60 days."
          variant="simple"
        />
      }
      otherWays={<OtherWaysToSendYourDocuments />}
    />
  );
};

SupplementalClaimFilesPage.propTypes = {
  clearSCErrors: PropTypes.func.isRequired,
  failedFiles: PropTypes.array.isRequired,
  progress: PropTypes.number.isRequired,
  submitFiles: PropTypes.func.isRequired,
  uploading: PropTypes.bool.isRequired,
  onCancelUpload: PropTypes.func.isRequired,
  appeal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    attributes: PropTypes.object.isRequired,
  }),
  uploadComplete: PropTypes.bool,
};

function mapStateToProps(state) {
  const claimsState = state.disability.status;

  return {
    failedFiles: claimsState.uploads.failedFiles,
    progress: claimsState.uploads.progress,
    uploadComplete: claimsState.uploads.uploadComplete,
    uploading: claimsState.uploads.uploading,
  };
}

const mapDispatchToProps = {
  submitFiles: submitClaimsEvidenceFiles,
  onCancelUpload: params =>
    cancelUpload({ ...params, apiName: CLAIMS_EVIDENCE_API_NAME }),
  clearSCErrors: clearSCUploadErrors,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SupplementalClaimFilesPage);

// mapDispatchToProps is exported so a test can check the api-name tagging.
export { SupplementalClaimFilesPage, mapDispatchToProps };
